import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Invoice } from '../../../types/domain';
import { formatBillingDate, formatCurrency } from '../types';
import {
  FileText,
  Printer,
  CheckCircle2,
  ShieldCheck,
} from 'lucide-react';

interface ReceiptViewerModalProps {
  selectedReceipt: Invoice | null;
  onClose: () => void;
  userEmail?: string;
  userFullName?: string;
}

export const ReceiptViewerModal: React.FC<ReceiptViewerModalProps> = ({
  selectedReceipt,
  onClose,
  userEmail,
  userFullName,
}) => {
  if (!selectedReceipt) return null;

  return (
    <Modal
      isOpen={Boolean(selectedReceipt)}
      onClose={onClose}
      title={`Tax Invoice & Receipt — ${selectedReceipt.invoice_number || ''}`}
      maxWidth="3xl"
    >
      <div className="space-y-4">
        {/* Modal Controls Toolbar (Hidden in Print) */}
        <div className="no-print flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-cf-bg p-3 rounded-lg border border-cf-border text-xs">
          <div className="flex items-center gap-2 text-cf-textSecondary min-w-0">
            <FileText size={15} className="text-cf-orange shrink-0" />
            <span className="text-xs text-cf-textSecondary leading-snug">
              Official electronic receipt for organization{' '}
              <strong className="text-cf-textPrimary font-semibold break-all">
                {selectedReceipt.organization_name || 'Organization'}
              </strong>
            </span>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 pt-1 sm:pt-0">
            <Button
              type="button"
              variant="primary"
              size="xs"
              onClick={() => window.print()}
              leftIcon={<Printer size={13} className="shrink-0" />}
              className="flex-1 sm:flex-none"
            >
              Print / Save PDF
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onClose}
              className="flex-1 sm:flex-none"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Authentic Printable Invoice Sheet */}
        <div
          id="printable-invoice"
          className="bg-white text-slate-900 border border-slate-200 rounded-lg p-4 sm:p-8 md:p-10 shadow-sm text-xs font-sans space-y-6 sm:space-y-8"
        >
          {/* Document Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 sm:gap-6 pb-5 sm:pb-6 border-b border-slate-200">
            {/* Brand / Vendor Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded bg-slate-900 text-white flex items-center justify-center font-mono font-bold text-sm">
                  E
                </div>
                <div className="leading-tight">
                  <span className="text-base font-bold text-slate-900 tracking-tight block">
                    ericius
                  </span>
                  <span className="text-xs text-slate-500 font-mono uppercase tracking-wider block">
                    Cloud Ingestion Engine
                  </span>
                </div>
              </div>

              <div className="text-xs text-slate-500 leading-relaxed pt-1">
                <p className="font-semibold text-slate-700">Ericius Technologies Inc.</p>
                <p>548 Market Street, Suite 29401</p>
                <p>San Francisco, CA 94104, USA</p>
                <p className="pt-0.5 font-mono text-xs">
                  Tax ID / EIN: US-94-3829101 • EU VAT: EU372009182
                </p>
                <p className="text-slate-600">billing@ericius.app • https://ericius.app</p>
              </div>
            </div>

            {/* Document Status & Meta */}
            <div className="sm:text-right space-y-2">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold font-mono uppercase">
                <CheckCircle2 size={13} />
                <span>PAID IN FULL</span>
              </div>

              <h2 className="text-xl font-bold text-slate-900 font-mono tracking-tight mt-1">
                {selectedReceipt.invoice_number}
              </h2>

              <div className="text-xs text-slate-600 space-y-1 font-mono">
                <div>
                  <span className="text-slate-400">Issue Date: </span>
                  <strong className="text-slate-800 font-semibold">{formatBillingDate(selectedReceipt.created_at)}</strong>
                </div>
                <div>
                  <span className="text-slate-400">Payment Date: </span>
                  <strong className="text-slate-800 font-semibold">{formatBillingDate(selectedReceipt.created_at)}</strong>
                </div>
                <div>
                  <span className="text-slate-400">Payment Method: </span>
                  <strong className="text-slate-800 font-semibold">
                    {selectedReceipt.payment_method?.brand
                      ? `${selectedReceipt.payment_method.brand.toUpperCase()} ending in ${selectedReceipt.payment_method.last4 || '4242'}`
                      : 'Credit Card ending in 4242'}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {/* Parties Details (Two Columns) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-xs leading-relaxed pb-4">
            <div className="space-y-1 bg-slate-50/70 p-4 rounded border border-slate-100">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block font-mono">
                Billed To (Customer)
              </span>
              <div className="font-bold text-slate-900 text-xs pt-0.5">
                {userFullName || 'Account Administrator'}
              </div>
              <div className="text-slate-700 font-medium">
                {selectedReceipt.organization_name || 'Organization'}
              </div>
              <div className="text-slate-500">{userEmail}</div>
              <div className="text-slate-400 font-mono text-xs pt-1">
                Organization ID: {selectedReceipt.organization_id}
              </div>
            </div>

            <div className="space-y-1 bg-slate-50/70 p-4 rounded border border-slate-100">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block font-mono">
                Billing Period & Coverage
              </span>
              <div className="font-bold text-slate-900 text-xs pt-0.5">
                {formatBillingDate(selectedReceipt.billing_period_start)} – {formatBillingDate(selectedReceipt.billing_period_end)}
              </div>
              <div className="text-slate-600">
                Recurring monthly subscription billing cycle.
              </div>
              <div className="text-slate-400 font-mono text-xs pt-1">
                Tax Treatment: B2B Electronic Services (Reverse Charge / 0% Tax)
              </div>
            </div>
          </div>

          {/* Itemized Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-900 text-slate-900 font-mono text-xs uppercase tracking-wider">
                  <th className="py-2.5 px-2 text-left font-bold">#</th>
                  <th className="py-2.5 px-2 text-left font-bold">Description & Services</th>
                  <th className="py-2.5 px-2 text-center font-bold">Period</th>
                  <th className="py-2.5 px-2 text-center font-bold">Qty</th>
                  <th className="py-2.5 px-2 text-right font-bold">Rate</th>
                  <th className="py-2.5 px-2 text-right font-bold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-xs">
                <tr>
                  <td className="py-4 px-2 font-mono text-slate-400 align-top">01</td>
                  <td className="py-4 px-2 align-top">
                    <div className="font-bold text-slate-900 text-xs uppercase tracking-wide">
                      Ericius {selectedReceipt.plan_tier} Plan Subscription
                    </div>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-md">
                      High-throughput form ingestion engine, relational JSONB storage, granular table-level RBAC,
                      and BYOP notification pipeline (SMTP / Webhook / Resend).
                    </p>
                  </td>
                  <td className="py-4 px-2 text-center font-mono text-slate-600 text-xs align-top whitespace-nowrap">
                    {formatBillingDate(selectedReceipt.billing_period_start)}<br />
                    <span className="text-slate-400">to</span> {formatBillingDate(selectedReceipt.billing_period_end)}
                  </td>
                  <td className="py-4 px-2 text-center font-mono text-slate-700 align-top">1</td>
                  <td className="py-4 px-2 text-right font-mono text-slate-700 align-top">
                    {formatCurrency(selectedReceipt.amount_cents, selectedReceipt.currency)}
                  </td>
                  <td className="py-4 px-2 text-right font-mono font-bold text-slate-900 align-top">
                    {formatCurrency(selectedReceipt.amount_cents, selectedReceipt.currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals & Tax Calculation Breakdown */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6 pt-4 border-t border-slate-200">
            <div className="text-xs text-slate-500 space-y-1.5 max-w-sm">
              <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                <ShieldCheck size={14} className="text-emerald-600" />
                <span>Payment Verification & Security</span>
              </div>
              <p className="leading-relaxed">
                Card payment cleared with authorization code <span className="font-mono text-slate-700">AUTH_{selectedReceipt.id.slice(0, 8).toUpperCase()}</span>.
                No further payment is required for this billing cycle.
              </p>
            </div>

            <div className="w-full sm:w-64 space-y-2 text-xs">
              <div className="flex justify-between text-slate-600 py-1">
                <span>Subtotal</span>
                <span className="font-mono font-medium">{formatCurrency(selectedReceipt.amount_cents, selectedReceipt.currency)}</span>
              </div>
              <div className="flex justify-between text-slate-600 py-1">
                <span>Tax / VAT (0.00%)</span>
                <span className="font-mono">$0.00</span>
              </div>
              <div className="border-t-2 border-slate-900 pt-2 flex justify-between items-baseline">
                <span className="font-bold text-slate-900 text-sm">Total Paid</span>
                <span className="font-mono font-bold text-slate-900 text-base">
                  {formatCurrency(selectedReceipt.amount_cents, selectedReceipt.currency)}
                </span>
              </div>
              <div className="flex justify-between text-xs text-emerald-700 font-medium pt-1">
                <span>Amount Due</span>
                <span className="font-mono font-bold">$0.00</span>
              </div>
            </div>
          </div>

          {/* Invoice Footer / Legal Disclaimers */}
          <div className="pt-6 border-t border-slate-200 text-xs text-slate-400 space-y-1 text-center sm:text-left font-mono">
            <p>
              Ericius Cloud Technologies Inc. • 548 Market St, Suite 29401, San Francisco, CA 94104 • Support: billing@ericius.app
            </p>
            <p className="text-slate-500">
              This document serves as an electronic tax invoice and receipt. Generated automatically by the Ericius billing subsystem.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
